import React, { useState, useRef, useEffect } from 'react'
import type { DeviceWithAssignments } from '@lura/shared'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'

interface DeviceSelectorProps {
  devices: DeviceWithAssignments[]
  onSelect: (device: DeviceWithAssignments) => void
  placeholder?: string
  className?: string
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  devices,
  onSelect,
  placeholder = 'Choose a device...',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Filter devices based on search query
  const filteredDevices = devices.filter(device => {
    const searchLower = searchQuery.toLowerCase()
    const serialNumber = device.serial_number.toLowerCase()
    const studyTitle = device.study_title?.toLowerCase() || ''
    return (
      serialNumber.includes(searchLower) || studyTitle.includes(searchLower)
    )
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
        setFocusedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const handleSelect = (device: DeviceWithAssignments) => {
    onSelect(device)
    setIsOpen(false)
    setSearchQuery('')
    setFocusedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev =>
          prev < filteredDevices.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && filteredDevices[focusedIndex]) {
          handleSelect(filteredDevices[focusedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchQuery('')
        setFocusedIndex(-1)
        break
    }
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`w-full text-left px-4 py-3 bg-white border-2 rounded-xl transition-all duration-200 ${
          isOpen
            ? 'border-primary-500 shadow-lg'
            : 'border-gray-300 hover:border-gray-400'
        } focus:outline-none focus:border-primary-500 focus:shadow-lg`}
      >
        <div className="flex items-center justify-between">
          <span className="text-gray-500">{placeholder}</span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-primary-500 rounded-xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value)
                setFocusedIndex(-1)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search devices..."
              className="w-full px-3 py-2 border-2 border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Devices List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredDevices.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {searchQuery
                  ? 'No devices found matching your search'
                  : 'No devices available'}
              </div>
            ) : (
              <>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                  {placeholder}
                </div>
                {filteredDevices.map((device, index) => (
                  <button
                    key={device.id}
                    type="button"
                    onClick={() => handleSelect(device)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    className={`w-full text-left px-4 py-3 transition-colors duration-150 ${
                      index === focusedIndex
                        ? 'bg-primary-50 text-primary-900'
                        : 'hover:bg-gray-50 text-gray-900'
                    } ${index < filteredDevices.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{device.serial_number}</span>
                      {device.is_test_device && <TestDeviceIcon />}
                    </div>
                    {device.study_title && (
                      <div className="text-sm text-gray-500 mt-0.5">
                        Currently in {device.study_title}
                      </div>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
